import { useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

export type IncomeBreakdownItem = {
  asset: string;
  incomeType: string;
  totalValue: number;
  totalGain: number;
  eventCount: number;
};

export type IncomeBreakdownSummary = {
  totalValue: number;
  totalEvents: number;
  uniqueAssets: number;
  uniqueTypes: number;
};

export type IncomeBreakdown = {
  taxYear: string;
  breakdown: IncomeBreakdownItem[];
  summary: IncomeBreakdownSummary;
};

export const useIncomeBreakdown = ({
  taxYear,
  enabled = true,
}: {
  taxYear: number;
  enabled?: boolean;
}) => {
  const { getToken, user } = useAuth();

  const getIncomeBreakdown = async (): Promise<IncomeBreakdown> => {
    const url = `/tax/income/breakdown/${taxYear}`;

    try {
      const token = await getToken();
      const res = await authorizedAxios(token).get(url);
      return res.data;
    } catch (error: any) {
      if (
        error?.response?.status === 403 &&
        ["subscription_required", "tax_year_locked"].includes(error?.response?.data?.error)
      ) {
        return undefined as any;
      }
      console.error("[useIncomeBreakdown] error", error);
      throw error;
    }
  };

  const { isLoading, error, data, refetch } = useQuery({
    queryKey: ["income_breakdown", taxYear],
    queryFn: getIncomeBreakdown,
    enabled: !!user?.email && enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return { isLoading, error, data, refetch };
};
